import { useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { snakeCase, values } from 'lodash'
import { Button, Divider, message, notification } from 'antd'
import { faEdit } from '@fortawesome/pro-solid-svg-icons'

import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { isValid } from '../../../utils/validators'
import { getRandomInt, isObject } from '../../../utils'
import { Quota, quotaGetters } from '../../../utils/entities/quota'
import QuotaForm from './QuotaForm'
import DataTable from '../../../Components/antd/Tables/DataTable/DataTable'
import { Action, DeleteAction } from '../../../Components/antd/Tables/DataTable/utils/actions'
import FormFooter from '../../../Components/antd/Forms/FormFooter'
import { dateGetters } from '../../../utils/entities/common'

import '../../../assets/quotas.scss'

export const BODY_STYLE = { display: 'flex', flexDirection: 'column', height: '93%', width: '100%' }
export const SELECT_STYLE = { width: '200px' }

message.config({
  top: 20,
  duration: 3,
  maxCount: 1,
  placement: 'topRight'
})

const BulkQuotaForm = ({ data, disabled, mode, sectors = [], sectionGroups = {}, studentPathways = [], validators, onDelete, onSubmit, onCancel }) => {
  const t = useSelector(selectTranslate)

  const [formData, setFormData] = useState(new Quota(isObject(data) ? data : { total: 0 }))
  const [quotas, setQuotas] = useState([])

  const handleQuotaSubmit = event => {
    const errors = isValid(formData, values(validators))

    if (errors.keys.length > 0) {
      const errorMessages = t('common_form.error_fields', {
        fields: errors.keys.map(key => t('common.' + snakeCase(key))).join(', ')
      })

      notification.error({ message: errorMessages })
    } else if (formData.startDate > formData.endDate) {
      message.error({ message: t('common_form.error_dates') })
    } else {
      let id = getRandomInt()

      while (quotas.some(q => q.id === id)) {
        id = getRandomInt()
      }

      setQuotas(current => [...current, new Quota({ ...formData, id: -id })])
    }
  }

  const handleSubmit = () => {
    setQuotas([])
    onSubmit(quotas)
  }

  const isSubmitDisabled = useMemo(() => (
    formData.school === null || formData.total === null || formData.startDate === null || formData.endDate === null || disabled || formData.sector === null
  ), [formData.school, formData.total, formData.startDate, formData.endDate, formData.sector, disabled])

  const actions = useMemo(() => [
    new Action({ icon: faEdit, title: t('common.edit'), onClick: setFormData }),
    new DeleteAction({ onDelete: quota => setQuotas(current => current.filter(q => q.id !== quota.id)) })
  ], [t, onDelete])
  const columns = useMemo(() => [
    { title: t('common.section_group'), key: quotaGetters.sectionGroupName },
    { title: t('common.section'), key: quotaGetters.sectionName },
    { title: t('common.school'), key: quotaGetters.schoolName },
    { title: t('common.year'), key: quotaGetters.yearName },
    { title: t('common.total'), key: quotaGetters.total },
    { title: t('common.start_date'), key: dateGetters.startDate },
    { title: t('common.end_date'), key: dateGetters.endDate }
  ], [t])

  return (
    <div>
      <div className='flex-column w-100 pd-12'>
        <QuotaForm
          data={formData}
          disabled={disabled}
          mode={mode}
          sectors={sectors}
          sectionGroups={sectionGroups}
          studentPathways={studentPathways}
          onChange={setFormData}
        />
        <div className='flex-row w-100 jc-end'>
          <Button onClick={handleQuotaSubmit} disabled={isSubmitDisabled}> {t('bulk_form.buttons.add')} </Button>
        </div>
      </div>

      <Divider className='divider-4' />

      <div className='w-100 pd-12'>
        <DataTable
          actions={actions}
          columns={columns}
          data={quotas}
        />
      </div>

      <Divider className='divider-4' />

      <FormFooter
        cancel={{ text: t('modal.cancel_button'), disabled }}
        remove={null}
        submit={{ text: t('modal.ok_button.save'), disabled: isSubmitDisabled || quotas.length === 0 }}
        onSubmit={handleSubmit}
        onCancel={onCancel}
      />
    </div>
  )
}

export default BulkQuotaForm
